/**
 * Waiting for a transaction's receipt without trusting one watcher (app#323), and seeing a
 * replacement the node does not show (app#378).
 *
 * viem's `waitForTransactionReceipt` follows a replaced transaction (the buyer sped it up or
 * cancelled it in the wallet) — which nothing else does — but it waits by watching the block
 * number and asking the node for the transaction first, and on a load-balanced public RPC a node
 * that lags behind its neighbours can leave that watcher silent. wagmi's wrapper hands viem
 * `timeout: 0`, so "silent" meant "for ever": a buyer saw "Transaction sent — confirming on the
 * network" and nothing after it, while the approve had been mined for minutes. Observed by Tim
 * ("Pay did not work the first time; the second time it did") and reproduced on 2026-10-15.
 *
 * So two waiters race: viem's, for the replacement case, and a plain poll of the receipt every
 * two seconds, which finishes the moment any node has it. A failure of viem's waiter does not
 * end the wait — the poll decides — and the whole thing has a deadline of its own, after which
 * the caller gets a sentence instead of silence. Pure: the readers come in as functions, so
 * the race is a unit test and not a network condition.
 *
 * And viem's waiter has a blind spot of its own (app#378): it looks for a replacement only once
 * the node has shown it the original *while it was pending* — `eth_getTransactionByHash` is how it
 * learns the sender and the nonce to watch. The public Base RPC is a replica with no view of the
 * pool: it answers `null` for a transaction that is waiting (three of three in the live check of
 * 2026-11-02, `pending` never above `latest` in the count either), so viem never looks, the poll of
 * the original hash finds nothing, and a payment the buyer sped up or cancelled in the wallet ended
 * as three minutes of "confirming" and the timeout sentence. The poll therefore carries a third
 * reader, fed by what that node *does* show: the signer's count of mined transactions
 * (`eth_getTransactionCount … 'latest'`) and the blocks. The caller says what it sent — who signs,
 * to whom, with what data and value. Once the count has moved while the original has no receipt,
 * the blocks mined since the wait began are read for the signer's transactions, and one that is not
 * the original is judged the way viem judges a replacement: the same `to`, `input` and `value` is
 * the action itself, sped up (`repriced`), and its receipt is the answer; a transfer of nothing to
 * the signer's own address `cancelled` it; anything else `replaced` it. The original's nonce is
 * never known here, so a wallet that had other transactions waiting is allowed for: a `cancelled` or
 * `replaced` verdict stands only after two more polls of the original find no receipt — a sender's
 * unrelated transaction, cancelled or mined while ours waited behind it, lets ours mine right after,
 * and that receipt wins, even off a node a poll behind. The cost in the common case is one more read
 * per poll (the count); the blocks are read only when the count moves, newest first, a dozen per
 * poll at most, and none of them twice.
 */

export interface ReceiptLike {
  transactionHash: string;
  status: 'success' | 'reverted';
}

/** A transaction as a block lists it — the fields the detection reads of it. */
export interface MinedTransactionLike {
  hash: string;
  from: string;
  to: string | null;
  input: string;
  value: bigint;
}

/** The transaction as the wallet was asked to send it: who signs it, and what it carries. */
export interface SentTransaction {
  from: string;
  to: string;
  input: string;
  value: bigint;
}

/**
 * Why another transaction stands for the one asked about (viem's words): `repriced` — the same
 * call, sped up in the wallet; `cancelled` — a transfer of nothing to the signer's own address;
 * `replaced` — any other transaction of the signer that took its place.
 */
export type ReplacementReason = 'repriced' | 'cancelled' | 'replaced';

/** What the detection reads of the chain, as functions — like `read`, so it is a unit test. */
export interface ReplacementWatchDeps {
  sent: SentTransaction;
  /**
   * A block the sent transaction cannot be in or before — the one holding the step just before it
   * (the approve a call follows). The window never reaches it, however far behind the node that
   * answered the first look was: without this, that earlier transaction of the signer would be
   * judged a replacement of a slow original.
   */
  floor?: bigint;
  /** `eth_getTransactionCount(from, 'latest')`: how many of the signer's transactions are mined. */
  readMinedCount: () => Promise<number>;
  /** `eth_blockNumber`, not from a cache. */
  readBlockNumber: () => Promise<bigint>;
  /** `eth_getBlockByNumber(n, true)`: the transactions of one block. */
  readBlockTransactions: (blockNumber: bigint) => Promise<readonly MinedTransactionLike[]>;
}

export interface AwaitReceiptDeps<R extends ReceiptLike> {
  hash: string;
  /** viem's waiter, with its replacement detection; must honour `timeoutMs` itself or run for ever. */
  wait: (hash: string) => Promise<R>;
  /** One read of the receipt: resolves with it, or with `null` while the node has none. */
  read: (hash: string) => Promise<R | null>;
  /**
   * The detection of a replacement the node does not show — present when the caller knows what it
   * sent. Absent, the wait is viem's waiter and the poll alone.
   */
  replacement?: ReplacementWatchDeps;
  /** Hears why the detection answers with another transaction's receipt, before it does. */
  onReplaced?: (reason: ReplacementReason, transaction: MinedTransactionLike) => void;
  timeoutMs?: number;
  intervalMs?: number;
  /** How many reads in a row may fail before the last failure is the answer (a node that is down, not behind). */
  maxReadFailures?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

export const RECEIPT_TIMEOUT_MS = 180_000;
export const RECEIPT_POLL_MS = 2_000;
export const RECEIPT_MAX_READ_FAILURES = 10;
/**
 * How many blocks one check reads, newest first. The count moves within a poll of the replacement
 * being mined, so it is in the newest block or two; the cap keeps a check inside the public RPC's
 * request budget. Blocks the cap left unread are read by the next polls, not forgotten: a page
 * suspended while the buyer was in the wallet app comes back to a gap of many blocks.
 */
export const REPLACEMENT_SCAN_BLOCKS = 12;
/**
 * How many more polls the original is read before a `cancelled` or `replaced` verdict stands. The
 * original's nonce is unknown: what the blocks show first may be the signer's older transaction that
 * ours waited behind — ours mines right after, and its receipt may sit on a node a poll behind.
 */
export const REPLACEMENT_GRACE_POLLS = 2;
/**
 * How many looks with every block read and nothing of the signer's found before a count that moved
 * is taken as explained (the original itself, mined before the first look and counted by a node
 * ahead of the one that gave the anchor): the receipt poll reports it, and reading every new block
 * until the deadline would only cost bandwidth.
 */
export const REPLACEMENT_EMPTY_LOOKS = 3;

/** The deadline passed with no receipt anywhere. The hash is kept for the message and the stash. */
export class ReceiptTimeoutError extends Error {
  constructor(public readonly hash: string) {
    super(
      `The network has not confirmed this transaction yet (tx ${hash}). Your funds are safe — reopen this screen in a minute to continue.`
    );
    this.name = 'ReceiptTimeoutError';
  }
}

const pause = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
const forever = new Promise<never>(() => {});

const sameHex = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/** viem's judgement of a transaction that stands for the one sent, given what was sent. */
export const judgeReplacement = (
  transaction: MinedTransactionLike,
  sent: SentTransaction
): ReplacementReason => {
  if (
    transaction.to !== null &&
    sameHex(transaction.to, sent.to) &&
    sameHex(transaction.input, sent.input) &&
    transaction.value === sent.value
  ) {
    return 'repriced';
  }

  if (
    transaction.to !== null &&
    sameHex(transaction.to, transaction.from) &&
    transaction.value === 0n
  ) {
    return 'cancelled';
  }

  return 'replaced';
};

/** A transaction of the signer found in the blocks, and what it is to the one sent. */
interface Replacement {
  reason: ReplacementReason;
  transaction: MinedTransactionLike;
}

/** `repriced` first — the action was done, whatever else the signer mined — then a cancel, then the rest. */
const RANK: Record<ReplacementReason, number> = { repriced: 0, cancelled: 1, replaced: 2 };

/**
 * The third reader. `check` is one step of it. The first look anchors the window at the head (never
 * below `floor`) and takes the signer's count; after that there is nothing to do while the count
 * stands. When it has moved, the blocks since the anchor that are still unread are read — newest
 * first, `REPLACEMENT_SCAN_BLOCKS` per look, the rest left for the next looks — for the signer's
 * transactions. The original among them means ours mined and nothing replaced it; otherwise the
 * best-ranked one is the answer. A count that moved with nothing in the blocks to show for it (a
 * node whose count runs ahead of its blocks, or the original mined before the first look) keeps the
 * reader on the new blocks for `REPLACEMENT_EMPTY_LOOKS` looks, then stands explained. Reads that
 * fail are not answers: the step yields nothing and the next poll tries again — a first look whose
 * count succeeds only then reads every block since its anchor, because what the signer mined
 * meanwhile is in the count it takes.
 */
const watchReplacement = (
  hash: string,
  { sent, floor, readMinedCount, readBlockNumber, readBlockTransactions }: ReplacementWatchDeps
) => {
  /** The head as of the last look — blocks above it are not in `unread` yet. */
  let seenHead: bigint | null = null;
  /** Blocks since the anchor not read yet, ascending. */
  let unread: bigint[] = [];
  /** The signer's count whose transactions are accounted for: mined before the wait, or looked at. */
  let explained: number | null = null;
  let looks = 0;
  let emptyLooks = 0;

  const check = async (): Promise<Replacement | null> => {
    looks += 1;

    try {
      if (seenHead === null) {
        // The anchor: the head first, then the count, so whatever is not in that count yet is in a
        // block after the anchor and inside every window read later. (The reverse order could lose a
        // transaction mined between the two reads below the window.) A look that could not read the
        // head anchors at the next look's — never behind it, and never below the floor: the blocks
        // before the wait hold the signer's own earlier transactions (the approve just before a
        // call), and a window reaching them would refuse a slow original over its own approve. A
        // replacement is the buyer's doing and takes longer than one poll, so nothing is lost.
        const head = await readBlockNumber();

        seenHead = floor !== undefined && floor > head ? floor : head;
      }

      const count = await readMinedCount();

      if (explained === null && looks === 1) {
        explained = count;

        return null;
      }

      // A first look that succeeded only now has blocks to catch up on: what the signer mined since
      // the anchor is in this count already, so every one of them is read.
      const unexplained = explained === null ? Number.POSITIVE_INFINITY : count - explained;

      if (unexplained <= 0) {
        return null;
      }

      // The count first, then the head: a transaction in this count is in a block up to the head,
      // so the window reaches it.
      const head = await readBlockNumber();

      for (let number = seenHead + 1n; number <= head; number += 1n) {
        unread.push(number);
      }

      if (head > seenHead) {
        seenHead = head;
      }

      const candidates: Replacement[] = [];
      let seen = 0;
      let ours = false;
      let read = 0;

      // Newest first — the replacement is in the newest block or two — a dozen per look; a block
      // read is taken off the list only once it answered, so a node that did not have it yet (or a
      // request that failed) leaves it for the next look.
      while (unread.length > 0 && read < REPLACEMENT_SCAN_BLOCKS && seen < unexplained) {
        const number = unread[unread.length - 1];
        const transactions = await readBlockTransactions(number);

        unread.pop();
        read += 1;

        for (const transaction of transactions) {
          if (!sameHex(transaction.from, sent.from)) {
            continue;
          }

          seen += 1;

          if (sameHex(transaction.hash, hash)) {
            // The original itself, with its receipt not yet on the node that answers the poll:
            // mined, so nothing stands in its place — the poll reports it.
            ours = true;
          } else {
            candidates.push({ reason: judgeReplacement(transaction, sent), transaction });
          }
        }
      }

      if (seen >= unexplained || explained === null) {
        // Every move of the count is accounted for (a first look catching up takes the count as
        // it is); what the list still holds is nobody's.
        unread = [];
        emptyLooks = 0;
      } else if (unread.length === 0) {
        // Everything since the anchor read and the count still unexplained: the original mined
        // before the first look, or a node ahead of its neighbours. A few more looks at the new
        // blocks, then it is taken as explained.
        emptyLooks += 1;
      }

      explained =
        explained === null || emptyLooks >= REPLACEMENT_EMPTY_LOOKS
          ? count
          : Math.min(count, explained + seen);

      if (ours) {
        return null;
      }

      candidates.sort((a, b) => RANK[a.reason] - RANK[b.reason]);

      return candidates[0] ?? null;
    } catch {
      return null;
    }
  };

  return { check };
};

export const awaitReceipt = async <R extends ReceiptLike>(
  deps: AwaitReceiptDeps<R>
): Promise<R> => {
  const {
    hash,
    wait,
    read,
    replacement,
    onReplaced,
    timeoutMs = RECEIPT_TIMEOUT_MS,
    intervalMs = RECEIPT_POLL_MS,
    maxReadFailures = RECEIPT_MAX_READ_FAILURES,
    sleep = pause,
    now = Date.now,
  } = deps;
  const deadline = now() + timeoutMs;
  let settled = false;
  let failures = 0;

  // A node that has no receipt yet (`null`) is asked again later; so is one that answered with
  // an error — a rate limit, a hiccup — up to a point: a node that fails every read for a while
  // is down or misconfigured, and that failure is the answer, not three minutes of silence.
  const readOnce = async (h: string): Promise<R | null> => {
    try {
      const receipt = await read(h);
      failures = 0;

      return receipt;
    } catch (error) {
      failures += 1;

      if (failures >= maxReadFailures) {
        throw error;
      }

      return null;
    }
  };

  const poll = async (): Promise<R> => {
    const watch = replacement ? watchReplacement(hash, replacement) : null;
    // What the blocks showed in place of the original. `repriced` is the action, mined under this
    // hash, and its receipt is the answer — the original is still read, in case what matched was a
    // twin of it (a retry with the same data, two tabs) and the original mined after all. `cancelled`
    // and `replaced` are a verdict only once `grace` polls of the original found no receipt. The
    // signer's unrelated transaction, mined while ours waited behind it, is what those polls are
    // for — ours mines right after, and its receipt wins.
    let replaced: (Replacement & { grace: number }) | null = null;

    while (!settled) {
      const receipt = await readOnce(hash);

      if (receipt) {
        return receipt;
      }

      if (replaced && replaced.grace > 0) {
        // One more look before the verdict — a better-ranked replacement mined meanwhile (the buyer
        // cancelled an older transaction, then sped ours up) is the answer over the one spotted first.
        const better = watch ? await watch.check() : null;

        if (better && RANK[better.reason] < RANK[replaced.reason]) {
          replaced = {
            ...better,
            grace: better.reason === 'repriced' ? 0 : REPLACEMENT_GRACE_POLLS,
          };
        } else {
          replaced.grace -= 1;
        }
      } else if (!replaced && watch) {
        const spotted = await watch.check();

        if (spotted) {
          replaced = {
            ...spotted,
            grace: spotted.reason === 'repriced' ? 0 : REPLACEMENT_GRACE_POLLS,
          };
        }
      }

      if (replaced && replaced.grace === 0) {
        const replacementReceipt = await readOnce(replaced.transaction.hash);

        if (replacementReceipt) {
          onReplaced?.(replaced.reason, replaced.transaction);

          return replacementReceipt;
        }
      }

      if (now() >= deadline) {
        throw new ReceiptTimeoutError(hash);
      }

      await sleep(intervalMs);
    }

    return forever;
  };

  // viem's waiter losing (an error of its own, a stall, even a synchronous throw) is not the wait
  // failing: the poll decides.
  const followed = Promise.resolve()
    .then(() => wait(hash))
    .catch(() => forever);

  try {
    return await Promise.race([followed, poll()]);
  } finally {
    settled = true;
  }
};
